import { useState, useCallback } from 'react';
import { useCADSheet } from '@/hooks/use-cad-sheet';
import { InputForm } from '@/components/cad/InputForm';
import { ImageGrid } from '@/components/cad/ImageGrid';
import { LivePreview } from '@/components/cad/LivePreview';
import { AllSheetsPanel } from '@/components/cad/AllSheetsPanel';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, Undo2, Redo2, FileText, PlusCircle, Download, RefreshCw, Save, X, Loader2, Link } from 'lucide-react';
import { LoadFromTaskButton } from '@/components/cad/LoadFromTaskButton';
import { syncCadToTask, getTaskByStyleNo, updateTaskStatus } from '@/utils/api';
import { storeSheet } from '@/lib/store-sheet';
import { toast } from 'sonner';

export default function CADSheetPage() {
  const sheet = useCADSheet();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('editor');
  const [linkedTaskId, setLinkedTaskId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Save popup state
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [cadUrl, setCadUrl] = useState('');
  const [markCompleted, setMarkCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleStored = useCallback((id) => {
    setEditingId(id);
    setRefreshKey(k => k + 1);
  }, []);

  const handleEditSheet = useCallback((data, id) => {
    sheet.loadSheet(data);
    setEditingId(id);
    setActiveTab('editor');
  }, [sheet]);

  const handleSaveAs = useCallback(() => {
    setEditingId(undefined);
    setShowSavePopup(true);
  }, []);

  const handleNewSheet = useCallback(() => {
    sheet.reset();
    setEditingId(undefined);
    setLinkedTaskId(null);
    setActiveTab('editor');
  }, [sheet]);

  // When loading from task, capture the task ID for sync
  const handleLoadFromTask = useCallback((data, taskId) => {
    sheet.loadSheet(data);
    setEditingId(undefined);
    setLinkedTaskId(taskId || null);
  }, [sheet]);

  // Open Save popup
  const handleSaveClick = useCallback(() => {
    setCadUrl('');
    setMarkCompleted(false);
    setShowSavePopup(true);
  }, []);

  // Execute save: store sheet + link to task + optional status change
  const handleConfirmSave = useCallback(async () => {
    setSaving(true);
    try {
      // 1. Store the CAD sheet in DB
      const result = await storeSheet(sheet.data, editingId);
      if (!result.success) {
        toast.error('Failed to save CAD sheet');
        setSaving(false);
        return;
      }
      const savedId = result.id;
      setEditingId(savedId);
      setRefreshKey(k => k + 1);

      // 2. Try to link to the task (by linkedTaskId or styleNo)
      let taskId = linkedTaskId;
      if (!taskId && sheet.data.styleNo) {
        try {
          const res = await getTaskByStyleNo(sheet.data.styleNo);
          taskId = res.data?.id;
        } catch { /* no matching task */ }
      }

      if (taskId) {
        // Sync CAD data back to task
        const updateData = {};
        if (sheet.data.styleNo) updateData.style_no = sheet.data.styleNo;
        if (sheet.data.customerName) updateData.client_name = sheet.data.customerName;
        if (sheet.data.size) updateData.size = sheet.data.size;
        if (sheet.data.netWeight) updateData.net_weight = parseFloat(sheet.data.netWeight) || null;
        if (sheet.data.grossWeight) updateData.gross_weight = parseFloat(sheet.data.grossWeight) || null;
        if (sheet.data.finishing) updateData.texture = sheet.data.finishing;
        if (sheet.data.engravingInstructions) updateData.engraving = sheet.data.engravingInstructions;
        if (sheet.data.otherDetails) updateData.special_instructions = sheet.data.otherDetails;
        if (sheet.data.designerName) updateData.designer_name = sheet.data.designerName;
        if (sheet.data.metals?.length > 0) {
          const m = sheet.data.metals[0];
          updateData.metal_karat = m.karat ? (m.karat.includes('K') ? m.karat : `${m.karat}K`) : null;
          updateData.metal_colour = m.color || null;
          updateData.metal_type = 'Gold';
          if (!updateData.net_weight && m.netWeight) updateData.net_weight = parseFloat(m.netWeight) || null;
          updateData.metal_details = sheet.data.metals.map(mt => ({
            metal_type: 'Gold', metal_colour: mt.color || 'Yellow',
            metal_karat: mt.karat ? (mt.karat.includes('K') ? mt.karat : `${mt.karat}K`) : '14K',
            weight: parseFloat(mt.netWeight) || null,
          }));
        }
        if (sheet.data.stones?.length > 0) {
          updateData.diamonds = sheet.data.stones.map(s => ({
            category: s.category || 'Accent Stone',
            stone_category: s.stone_category || s.ownership || 'Natural',
            stone_shape: s.stone_shape || s.type || 'Round',
            stone_quality: s.stone_quality || s.quality || '',
            setting_type: s.setting_type || 'Prong',
            size_mm: s.size_mm || s.size || '',
            weight_per_stone: parseFloat(s.weight_per_stone ?? s.weight) || 0,
            quantity: parseInt(s.quantity) || 0,
            total_weight: (parseFloat(s.weight_per_stone ?? s.weight) || 0) * (parseInt(s.quantity) || 0),
          }));
        }
        // Save the CAD URL to the task
        if (cadUrl.trim()) {
          updateData.cad_url = cadUrl.trim();
        }

        try {
          await syncCadToTask(taskId, updateData);
        } catch (err) {
          console.error('Sync to task failed:', err);
        }

        // 3. Optionally change status to CAD Completed
        if (markCompleted) {
          try {
            await updateTaskStatus(taskId, { status: 'cad_completed' });
            toast.success('Status changed to CAD Completed');
          } catch (err) {
            toast.error('Could not change status: ' + (err.response?.data?.detail || err.message));
          }
        }
      }

      toast.success(editingId ? 'CAD sheet updated & linked' : 'CAD sheet saved & linked');
      setShowSavePopup(false);
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }, [sheet.data, editingId, linkedTaskId, cadUrl, markCompleted]);

  // Sync CAD sheet data back to the task (quick sync without save popup)
  const handleSyncToTask = useCallback(async () => {
    const styleNo = sheet.data.styleNo;
    if (!styleNo && !linkedTaskId) {
      toast.error('No linked task. Load a task first or enter a Style No.');
      return;
    }
    setSyncing(true);
    try {
      let taskId = linkedTaskId;
      if (!taskId && styleNo) {
        const res = await getTaskByStyleNo(styleNo);
        taskId = res.data.id;
      }
      if (!taskId) {
        toast.error('Could not find linked task');
        setSyncing(false);
        return;
      }
      const updateData = {};
      if (sheet.data.styleNo) updateData.style_no = sheet.data.styleNo;
      if (sheet.data.customerName) updateData.client_name = sheet.data.customerName;
      if (sheet.data.size) updateData.size = sheet.data.size;
      if (sheet.data.netWeight) updateData.net_weight = parseFloat(sheet.data.netWeight) || null;
      if (sheet.data.grossWeight) updateData.gross_weight = parseFloat(sheet.data.grossWeight) || null;
      if (sheet.data.finishing) updateData.texture = sheet.data.finishing;
      if (sheet.data.engravingInstructions) updateData.engraving = sheet.data.engravingInstructions;
      if (sheet.data.otherDetails) updateData.special_instructions = sheet.data.otherDetails;
      if (sheet.data.designerName) updateData.designer_name = sheet.data.designerName;
      if (sheet.data.metals?.length > 0) {
        const m = sheet.data.metals[0];
        updateData.metal_karat = m.karat ? (m.karat.includes('K') ? m.karat : `${m.karat}K`) : null;
        updateData.metal_colour = m.color || null;
        updateData.metal_type = 'Gold';
        if (!updateData.net_weight && m.netWeight) updateData.net_weight = parseFloat(m.netWeight) || null;
        updateData.metal_details = sheet.data.metals.map(mt => ({
          metal_type: 'Gold', metal_colour: mt.color || 'Yellow',
          metal_karat: mt.karat ? (mt.karat.includes('K') ? mt.karat : `${mt.karat}K`) : '14K',
          weight: parseFloat(mt.netWeight) || null,
        }));
      }
      if (sheet.data.stones?.length > 0) {
        updateData.diamonds = sheet.data.stones.map(s => ({
          category: s.category || 'Accent Stone',
          stone_category: s.stone_category || s.ownership || 'Natural',
          stone_shape: s.stone_shape || s.type || 'Round',
          stone_quality: s.stone_quality || s.quality || '',
          setting_type: s.setting_type || 'Prong',
          size_mm: s.size_mm || s.size || '',
          weight_per_stone: parseFloat(s.weight_per_stone ?? s.weight) || 0,
          quantity: parseInt(s.quantity) || 0,
          total_weight: (parseFloat(s.weight_per_stone ?? s.weight) || 0) * (parseInt(s.quantity) || 0),
        }));
      }
      await syncCadToTask(taskId, updateData);
      toast.success('Task updated from CAD sheet data');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync to task');
    } finally {
      setSyncing(false);
    }
  }, [sheet.data, linkedTaskId]);

  return (
    <CollapsibleLayout>
      <div className="flex flex-col h-[calc(100vh-48px)]" data-testid="cad-sheet-page">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src="/ks-logo.png" alt="K's" className="h-7 w-7 object-contain" />
            <h1 className="text-base font-bold text-primary tracking-wide">CAD Sheet Designer</h1>
            <div className="flex border border-border rounded-md overflow-hidden ml-4">
              <button
                onClick={() => setActiveTab('editor')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${activeTab === 'editor' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                data-testid="cad-tab-editor"
              >
                <PlusCircle className="h-3 w-3 inline mr-1" />Editor
              </button>
              <button
                onClick={() => setActiveTab('sheets')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${activeTab === 'sheets' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                data-testid="cad-tab-sheets"
              >
                <FileText className="h-3 w-3 inline mr-1" />All Sheets
              </button>
            </div>
          </div>
          {activeTab === 'editor' && (
            <div className="flex items-center gap-2">
              {(linkedTaskId || sheet.data.styleNo) && (
                <Button size="sm" variant="outline" onClick={handleSyncToTask} disabled={syncing} className="h-7 text-xs" data-testid="cad-sync-task-btn">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : 'Sync to Task'}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} className="h-7 text-xs" data-testid="cad-preview-btn">
                <Eye className="h-3.5 w-3.5 mr-1" />Preview & Export
              </Button>
              <Button size="sm" onClick={handleSaveClick} className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="cad-save-main-btn">
                <Save className="h-3.5 w-3.5 mr-1" />Save & Link
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === 'editor' ? (
          <div className="flex-1 overflow-hidden flex">
            {/* Left Toolbar */}
            <div className="w-11 bg-muted/40 border-r border-border flex flex-col items-center py-3 gap-2 flex-shrink-0" data-testid="cad-left-toolbar">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={sheet.undo}
                disabled={!sheet.canUndo()}
                title="Undo"
                data-testid="cad-undo-btn"
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={sheet.redo}
                disabled={!sheet.canRedo()}
                title="Redo"
                data-testid="cad-redo-btn"
              >
                <Redo2 className="h-4 w-4" />
              </Button>
              <div className="w-6 border-t border-border my-1" />
              <LoadFromTaskButton
                onLoadSheet={(data, taskId) => handleLoadFromTask(data, taskId)}
                compact
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleNewSheet}
                title="New Sheet"
                data-testid="cad-new-btn"
              >
                <PlusCircle className="h-4 w-4" />
              </Button>
            </div>

            {/* Left: Input Form */}
            <div className="w-[380px] border-r border-border overflow-y-auto p-4 flex-shrink-0">
              <InputForm
                data={sheet.data}
                onUpdateField={sheet.updateField}
                onAddStone={sheet.addStone}
                onImportStones={sheet.importStones}
                onUpdateStone={sheet.updateStone}
                onRemoveStone={sheet.removeStone}
                onAddMetal={sheet.addMetal}
                onUpdateMetal={sheet.updateMetal}
                onRemoveMetal={sheet.removeMetal}
                onAddFinding={sheet.addFinding}
                onUpdateFinding={sheet.updateFinding}
                onRemoveFinding={sheet.removeFinding}
                onSetGrossWeightManual={sheet.setGrossWeightManual}
                onSetEngravingImage={sheet.setEngravingImage}
                onRemoveEngravingImage={sheet.removeEngravingImage}
                onAddCustomerImage={sheet.addCustomerImage}
                onRemoveCustomerImage={sheet.removeCustomerImage}
                onReset={sheet.reset}
              />
            </div>
            {/* Right: Image Grid */}
            <div className="flex-1 overflow-y-auto">
              <ImageGrid
                images={sheet.data.images}
                onSetImage={sheet.setImage}
                onRemoveImage={sheet.removeImage}
                onAddImageSlot={sheet.addImageSlot}
                onRemoveImageSlot={sheet.removeImageSlot}
                onUpdateLabel={sheet.updateImageLabel}
                onAddAnnotation={sheet.addAnnotation}
                onRemoveAnnotation={sheet.removeAnnotation}
                onUpdateAnnotation={sheet.updateAnnotation}
                onAddTextBox={sheet.addTextBox}
                onRemoveTextBox={sheet.removeTextBox}
                onUpdateTextBox={sheet.updateTextBox}
                onAddHighlight={sheet.addHighlight}
                onRemoveHighlight={sheet.removeHighlight}
                onUpdateHighlight={sheet.updateHighlight}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <AllSheetsPanel onEditSheet={handleEditSheet} refreshKey={refreshKey} />
          </div>
        )}

        {/* Preview Modal — export only, no save inside */}
        <LivePreview
          data={sheet.data}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />

        {/* Save & Link Popup */}
        {showSavePopup && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center" data-testid="cad-save-popup-overlay">
            <div className="bg-card border border-border rounded-xl shadow-2xl w-[420px] p-6" data-testid="cad-save-popup">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                  <Save className="h-4 w-4 text-green-600" />Save CAD Sheet
                </h3>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowSavePopup(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* CAD URL Input */}
                <div>
                  <Label className="text-xs font-medium">CAD File URL <span className="text-muted-foreground">(local DB location)</span></Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Link className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <Input
                      value={cadUrl}
                      onChange={e => setCadUrl(e.target.value)}
                      placeholder="e.g. Z:\CAD Files\R2-216680.3dm"
                      className="h-8 text-sm"
                      data-testid="cad-url-input"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Where this CAD file is saved on your local system/network</p>
                </div>

                {/* Status Change Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
                  <Checkbox
                    id="markCompleted"
                    checked={markCompleted}
                    onCheckedChange={setMarkCompleted}
                    data-testid="cad-mark-completed-toggle"
                  />
                  <div>
                    <label htmlFor="markCompleted" className="text-sm font-medium cursor-pointer">Mark as CAD Completed</label>
                    <p className="text-[10px] text-muted-foreground">Instantly change task status to "CAD Completed"</p>
                  </div>
                </div>

                {/* Info */}
                <div className="text-xs text-muted-foreground bg-blue-50 border border-blue-100 rounded-lg p-2.5">
                  This will save the CAD sheet + views to the Sheets tab and link them to the main task summary{linkedTaskId || sheet.data.styleNo ? ` (${sheet.data.styleNo || 'linked task'})` : ''}.
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-border">
                <Button variant="ghost" size="sm" onClick={() => setShowSavePopup(false)} className="h-8 text-xs">Cancel</Button>
                <Button size="sm" onClick={handleConfirmSave} disabled={saving} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="cad-confirm-save-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  {saving ? 'Saving...' : 'Save & Link'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsibleLayout>
  );
}
